import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Stack,
  Alert,
  Divider,
  Avatar,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Cancel as CancelIcon,
  Security as SecurityIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { useQuery, useMutation } from '@apollo/client';
import { GET_ME } from '../graphql/queries';
import { UPDATE_ME, CHANGE_MY_PASSWORD } from '../graphql/mutations';
import { useAuth } from '../context/AuthContext';

const UserProfile = () => {
  // eslint-disable-next-line no-unused-vars
  const { user, setUser } = useAuth();

  // Profile edit state
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    username: '',
    email: '',
  });
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');

  // Password change state
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  // Fetch current user data
  const { data, loading, refetch } = useQuery(GET_ME);

  // Mutations
  const [updateMe, { loading: updateLoading }] = useMutation(UPDATE_ME, {
    onCompleted: (data) => {
      setProfileSuccess('Профилът е обновен успешно');
      setProfileError('');
      setIsEditingProfile(false);
      // Update auth context
      if (data.updateMe) {
        setUser(data.updateMe);
        localStorage.setItem('authUser', JSON.stringify(data.updateMe));
      }
      refetch();
    },
    onError: (error) => {
      setProfileError(error.message);
      setProfileSuccess('');
    },
  });

  const [changeMyPassword, { loading: passwordLoading }] = useMutation(CHANGE_MY_PASSWORD, {
    onCompleted: () => {
      setPasswordSuccess('Паролата е сменена успешно');
      setPasswordError('');
      setIsChangingPassword(false);
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    },
    onError: (error) => {
      setPasswordError(error.message);
      setPasswordSuccess('');
    },
  });

  // Initialize form when data loads
  useEffect(() => {
    if (data?.me) {
      setProfileForm({
        username: data.me.username || '',
        email: data.me.email || '',
      });
    }
  }, [data]);

  const handleProfileSubmit = (e) => {
    e.preventDefault();
    setProfileError('');
    setProfileSuccess('');

    if (!profileForm.username.trim()) {
      setProfileError('Потребителското име е задължително');
      return;
    }
    if (!profileForm.email.trim()) {
      setProfileError('Имейл адресът е задължителен');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileForm.email)) {
      setProfileError('Невалиден имейл адрес');
      return;
    }

    updateMe({
      variables: {
        input: {
          username: profileForm.username,
          email: profileForm.email,
        },
      },
    });
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (!passwordForm.currentPassword) {
      setPasswordError('Въведете текущата парола');
      return;
    }
    if (!passwordForm.newPassword) {
      setPasswordError('Въведете новата парола');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      setPasswordError('Новата парола трябва да е поне 6 символа');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('Паролите не съвпадат');
      return;
    }

    changeMyPassword({
      variables: {
        input: {
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword,
        },
      },
    });
  };

  const cancelProfileEdit = () => {
    setIsEditingProfile(false);
    setProfileError('');
    setProfileSuccess('');
    if (data?.me) {
      setProfileForm({
        username: data.me.username || '',
        email: data.me.email || '',
      });
    }
  };

  const cancelPasswordChange = () => {
    setIsChangingPassword(false);
    setPasswordError('');
    setPasswordSuccess('');
    setPasswordForm({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'SUPER_ADMIN': return 'Супер Администратор';
      case 'ADMIN': return 'Администратор';
      case 'USER': return 'Потребител';
      case 'ACCOUNTANT': return 'Счетоводител';
      default: return role;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'SUPER_ADMIN': return 'error';
      case 'ADMIN': return 'warning';
      case 'USER': return 'primary';
      case 'ACCOUNTANT': return 'info';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const currentUser = data?.me;

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PersonIcon fontSize="large" />
        Моят профил
      </Typography>

      <Stack spacing={3}>
        {/* Profile Info Card */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: getRoleColor(currentUser?.role) + '.main',
                  fontSize: '2rem',
                }}
              >
                {currentUser?.username?.charAt(0).toUpperCase()}
              </Avatar>
              <Box sx={{ ml: 2 }}>
                <Typography variant="h5">{currentUser?.username}</Typography>
                <Chip
                  label={getRoleLabel(currentUser?.role)}
                  color={getRoleColor(currentUser?.role)}
                  size="small"
                  icon={<SecurityIcon />}
                />
              </Box>
            </Box>

            {!isEditingProfile ? (
              <>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon color="action" />
                    <Typography variant="body1">
                      <strong>Потребителско име:</strong> {currentUser?.username}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon color="action" />
                    <Typography variant="body1">
                      <strong>Имейл:</strong> {currentUser?.email}
                    </Typography>
                  </Box>
                  {currentUser?.company && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BusinessIcon color="action" />
                      <Typography variant="body1">
                        <strong>Фирма:</strong> {currentUser.company.name}
                      </Typography>
                    </Box>
                  )}
                </Stack>
                <Box sx={{ mt: 3 }}>
                  <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={() => setIsEditingProfile(true)}
                  >
                    Редактирай профил
                  </Button>
                </Box>
              </>
            ) : (
              <form onSubmit={handleProfileSubmit}>
                {profileError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {profileError}
                  </Alert>
                )}
                {profileSuccess && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    {profileSuccess}
                  </Alert>
                )}
                <Stack spacing={2}>
                  <TextField
                    label="Потребителско име"
                    value={profileForm.username}
                    onChange={(e) => setProfileForm({ ...profileForm, username: e.target.value })}
                    fullWidth
                    required
                    InputProps={{
                      startAdornment: <PersonIcon color="action" sx={{ mr: 1 }} />,
                    }}
                  />
                  <TextField
                    label="Имейл адрес"
                    type="email"
                    value={profileForm.email}
                    onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                    fullWidth
                    required
                    InputProps={{
                      startAdornment: <EmailIcon color="action" sx={{ mr: 1 }} />,
                    }}
                  />
                </Stack>
                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    startIcon={updateLoading ? <CircularProgress size={20} /> : <SaveIcon />}
                    disabled={updateLoading}
                  >
                    Запази
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={cancelProfileEdit}
                    disabled={updateLoading}
                  >
                    Отказ
                  </Button>
                </Box>
              </form>
            )}
          </CardContent>
        </Card>

        {/* Password Change Card */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LockIcon />
              Смяна на парола
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {!isChangingPassword ? (
              <Button
                variant="outlined"
                startIcon={<LockIcon />}
                onClick={() => setIsChangingPassword(true)}
              >
                Промени паролата
              </Button>
            ) : (
              <form onSubmit={handlePasswordSubmit}>
                {passwordError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {passwordError}
                  </Alert>
                )}
                {passwordSuccess && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    {passwordSuccess}
                  </Alert>
                )}
                <Stack spacing={2}>
                  <TextField
                    label="Текуща парола"
                    type="password"
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                    fullWidth
                    required
                  />
                  <TextField
                    label="Нова парола"
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                    fullWidth
                    required
                    helperText="Минимум 6 символа"
                  />
                  <TextField
                    label="Потвърди новата парола"
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                    fullWidth
                    required
                  />
                </Stack>
                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    startIcon={passwordLoading ? <CircularProgress size={20} /> : <SaveIcon />}
                    disabled={passwordLoading}
                  >
                    Смени паролата
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={cancelPasswordChange}
                    disabled={passwordLoading}
                  >
                    Отказ
                  </Button>
                </Box>
              </form>
            )}
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
};

export default UserProfile;
